import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCheckCircle,
  faFireFlameCurved,
  faPhone,
  faShieldHalved,
  faStar,
  faAward,
} from "@fortawesome/free-solid-svg-icons";
import { contattiAzienda } from "../../../utils/data";
import { DEFAULT_CONFIG } from "../../../utils/form-submissions";

export type DirectCallLandingName =
  | "landing-direct-call-1"
  | "landing-direct-call-2"
  | "landing-direct-call-3"
  | "landing-direct-call-4";

interface DirectCallLandingViewProps {
  landingName: DirectCallLandingName;
  phoneDisplay: string;
  phoneTel: string;
}

const COOKIE_CONSENT_KEY = "cookieConsent";

const API_BASE =
  DEFAULT_CONFIG.backendApiUrl || "https://back.risparmiami.it/api";
const STORAGE_URL = API_BASE.replace(/\/api\/?$/, "/storage");
const PROVIDERS_URL = `${API_BASE.replace(/\/$/, "")}/providers`;

const ENERGY_SERVICE_TYPES = new Set(["luce", "gas", "dual"]);
const LOOP_COPIES = 4;

interface Provider {
  id: number;
  name: string;
  image_providers?: string | null;
  is_active?: boolean;
  service_types?: string[];
}

const TRUST_CHIPS = [
  "Consulenza gratuita",
  "Senza impegno",
  "Azienda italiana",
];

const WHY_CALL = [
  {
    icon: faBolt,
    title: "Luce e gas",
    text: "Un consulente ti aiuta subito sulle utenze di casa.",
  },
  {
    icon: faShieldHalved,
    title: "Confronto indipendente",
    text: "Non siamo il call center di un fornitore. Confrontiamo le offerte.",
  },
  {
    icon: faAward,
    title: "Risparmi solo se conviene",
    text: "Ti diciamo se paghi troppo. Attivi solo se ti torna.",
  },
];

const OPTIMIZED_PROVIDER_LOGOS: Record<string, string> = {
  "providers/oe5Nhn8T1VG7fJNet3jGLTg0uqHTk5HmN1ut9MYf.png":
    "/landing-direct-call/providers/plenitude.webp",
  "providers/XryIjlUU27nDdfZsiwXsHx8Fn7iqkATg9VTKXs8c.png":
    "/landing-direct-call/providers/onda.webp",
  "providers/impfYi1nKJOH2qaiKsFtWIdh3kR4UgeX5J6PQ7eT.png":
    "/landing-direct-call/providers/heracomm.webp",
  "providers/ikqC4YM0owdOedixne2fjUc02HguDK4TySW0NFzA.png":
    "/landing-direct-call/providers/estenergy.webp",
};

const providerLogoSrc = (imagePath: string) => {
  const normalized = imagePath.replace(/^\//, "");
  return (
    OPTIMIZED_PROVIDER_LOGOS[normalized] ?? `${STORAGE_URL}/${normalized}`
  );
};

const TOPICS = [
  {
    title: "Confronta le offerte per il tuo contratto luce e gas",
    paragraphs: [
      "Vuoi valutare un nuovo contratto luce e gas o capire se la tua offerta attuale è ancora conveniente? Servizio Bolletta ti aiuta a confrontare le offerte disponibili dei fornitori partner, in base alle esigenze della tua fornitura luce e gas.",
      "Durante la consulenza telefonica puoi approfondire i costi in bolletta e le condizioni delle offerte, per valutare quale compagnia luce o gas scegliere. Tieni a portata di mano una bolletta recente per facilitare il confronto.",
      "La consulenza è gratuita e senza obbligo di sottoscrizione.",
    ],
  },
  {
    title: "Mercato tutelato e mercato libero luce e gas",
    paragraphs: [
      "Hai sentito parlare di mercato tutelato e mercato libero e vuoi capire quali opzioni hai per la tua fornitura? Chiama Servizio Bolletta e scopri come possiamo aiutarti a risparmiare. La consulenza telefonica è gratuita e senza impegno.",
    ],
  },
  {
    title: "Voltura, subentro e attivazione luce e gas",
    paragraphs: [
      "Stai valutando una voltura, un subentro o una nuova attivazione di luce o gas? Chiama Servizio Bolletta e scopri come possiamo aiutarti a risparmiare. La consulenza telefonica è gratuita e senza impegno.",
    ],
  },
];

const REVIEWS = [
  {
    name: "Elena M.",
    city: "Milano",
    text: "Ho chiamato e in pochi minuti ho capito se stavo pagando troppo. Zero pressione.",
  },
  {
    name: "Paolo R.",
    city: "Roma",
    text: "Mi hanno spiegato luce e gas in modo chiaro. Poi ho deciso io, senza impegno.",
  },
  {
    name: "Sara B.",
    city: "Torino",
    text: "Consulenza rapida e concreta. Meglio di dieci pagine di offerte da leggere da sola.",
  },
];

const HOURS = `Lun–Ven ${contattiAzienda.orarioLavorativo} · Sab ${contattiAzienda.orarioSabato}`;

const isEnergyProvider = (provider: Provider) =>
  (provider.service_types ?? []).some((type) =>
    ENERGY_SERVICE_TYPES.has(type.toLowerCase()),
  );

const ClickArrow = () => (
  <div
    className="pointer-events-none relative mx-auto mb-1 flex w-full max-w-[17rem] justify-center"
    aria-hidden
  >
    <div className="ldc-arrow flex flex-col items-center">
      <span className="-rotate-6 text-[1.05rem] font-extrabold tracking-tight text-[#F5F749] drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)] sm:text-xl">
        Clicca qui
      </span>
      <svg
        viewBox="0 0 88 78"
        className="h-[4.4rem] w-[4.9rem] sm:h-[5rem] sm:w-[5.6rem]"
        fill="none"
      >
        <path
          d="M44 6c-14 18-4 28 6 38 8 8 8 16-2 26"
          stroke="#FFFFFF"
          strokeWidth="5.5"
          strokeLinecap="round"
        />
        <path
          d="M32 54l16 20 20-16"
          stroke="#FFFFFF"
          strokeWidth="5.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </div>
);

const pushDataLayer = (payload: Record<string, unknown>) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

const trackTelClick = (
  landingName: DirectCallLandingName,
  phoneTel: string,
  cta: "hero" | "bar",
) => {
  pushDataLayer({
    event: "landing_direct_call_tel_click",
    landing_name: landingName,
    phone_number: phoneTel.replace(/^tel:/, ""),
    cta_position: cta,
    page_url:
      typeof window !== "undefined" ? window.location.href.split("#")[0] : "",
  });
};

const CallButton = ({
  landingName,
  phoneDisplay,
  phoneTel,
  size = "hero",
}: {
  landingName: DirectCallLandingName;
  phoneDisplay: string;
  phoneTel: string;
  size?: "hero" | "bar";
}) => {
  const onClick = () => trackTelClick(landingName, phoneTel, size);

  if (size === "bar") {
    return (
      <a
        href={phoneTel}
        onClick={onClick}
        className="ldc-call-btn inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-[#16A34A] px-4 text-white shadow-[0_8px_24px_rgba(22,163,74,0.4)]"
      >
        <FontAwesomeIcon icon={faPhone} className="h-5 w-5 shrink-0" />
        <span className="text-base font-black tracking-tight">Chiama ora</span>
      </a>
    );
  }

  return (
    <a
      href={phoneTel}
      onClick={onClick}
      className="ldc-call-btn group relative flex min-h-[4.5rem] w-full items-center justify-center overflow-hidden rounded-[1.35rem] bg-[#16A34A] px-5 text-white shadow-[0_16px_40px_-8px_rgba(22,163,74,0.65)] transition-transform active:scale-[0.98] sm:min-h-[5rem]"
    >
      <span className="ldc-call-shine pointer-events-none absolute inset-0" />
      <span className="relative flex items-center gap-3.5">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/25 sm:h-14 sm:w-14">
          <FontAwesomeIcon icon={faPhone} className="h-6 w-6 sm:h-7 sm:w-7" />
        </span>
        <span className="text-left">
          <span className="block text-[1.45rem] font-black leading-none tracking-tight sm:text-[1.7rem]">
            Chiama ora
          </span>
          <span className="mt-1.5 block text-sm font-bold tracking-wide text-white/95 sm:text-base">
            {phoneDisplay}
          </span>
        </span>
      </span>
    </a>
  );
};

const PartnerLogos = () => {
  const [providers, setProviders] = useState<Provider[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch(PROVIDERS_URL)
      .then((response) => response.json())
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        setProviders(
          data.filter(
            (provider: Provider) =>
              provider.is_active !== false && isEnergyProvider(provider),
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setProviders([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (providers.length === 0) return null;

  const loopedProviders = Array.from(
    { length: LOOP_COPIES },
    () => providers,
  ).flat();

  return (
    <div className="mt-6" aria-label="Fornitori partner luce e gas">
      <p className="text-[13px] font-semibold text-white/65">
        Ti colleghiamo ai migliori fornitori.
      </p>

      <div className="relative mt-3 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-[#0B0B0D] to-transparent"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[#0B0B0D] to-transparent"
          aria-hidden
        />

        <div className="ldc-logo-track flex w-max items-center gap-2.5 py-0.5">
          {loopedProviders.map((provider, index) => (
            <div
              key={`${provider.id}-${index}`}
              className="flex h-[3.65rem] w-[7.25rem] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white px-2"
              aria-hidden={index >= providers.length}
            >
              {provider.image_providers ? (
                <img
                  src={providerLogoSrc(provider.image_providers)}
                  alt={index < providers.length ? provider.name : ""}
                  width={120}
                  height={120}
                  decoding="async"
                  className="h-full w-full scale-[1.35] object-contain"
                />
              ) : (
                <span className="text-center text-[11px] font-bold leading-tight text-slate-500">
                  {provider.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .ldc-logo-track {
          animation: ldc-logo-marquee 18s linear infinite;
        }
        @keyframes ldc-logo-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-25%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ldc-logo-track {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

const DirectCallLandingView: React.FC<DirectCallLandingViewProps> = ({
  landingName,
  phoneDisplay,
  phoneTel,
}) => {
  const [cookieResolved, setCookieResolved] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(localStorage.getItem(COOKIE_CONSENT_KEY));
  });

  useEffect(() => {
    pushDataLayer({
      event: "landing_direct_call_page_view",
      landing_name: landingName,
      page_url:
        typeof window !== "undefined" ? window.location.href.split("#")[0] : "",
    });
  }, [landingName]);

  useEffect(() => {
    const syncConsent = () =>
      setCookieResolved(Boolean(localStorage.getItem(COOKIE_CONSENT_KEY)));

    const onStorage = (e: StorageEvent) => {
      if (e.key === COOKIE_CONSENT_KEY) syncConsent();
    };

    window.addEventListener("storage", onStorage);
    const interval = window.setInterval(syncConsent, 500);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(interval);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Chiama per assistenza luce e gas | Servizio Bolletta</title>
        <meta
          name="description"
          content="Assistenza gratuita su luce e gas. Chiama un consulente e scopri se puoi risparmiare. Senza impegno."
        />
        <link
          rel="canonical"
          href={`https://serviziobolletta.it/${landingName}/`}
        />
      </Helmet>

      <div className="min-h-[100dvh] bg-[#0B0B0D] pb-[5.25rem] text-white">
        <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B0B0D]/92 backdrop-blur-md">
          <div className="mx-auto flex h-[3.65rem] max-w-lg items-center justify-between px-4">
            <div className="rounded-xl bg-white px-2.5 py-1">
              <img
                src="/landing-direct-call/logo.webp"
                alt="Servizio Bolletta"
                width={56}
                height={56}
                decoding="async"
                className="h-7 w-auto"
              />
            </div>
            <p className="flex items-center gap-1.5 text-[11px] font-semibold text-white/70">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#22C55E]" />
              </span>
              In linea ora
            </p>
          </div>
        </header>

        <main>
          <section className="relative overflow-hidden px-4 pb-10 pt-7 sm:pt-10">
            <div className="pointer-events-none absolute -left-24 top-8 h-56 w-56 rounded-full bg-[#16A34A]/18 blur-3xl" />
            <div className="pointer-events-none absolute -right-16 bottom-0 h-64 w-64 rounded-full bg-[#F5F749]/10 blur-3xl" />

            <div className="relative mx-auto max-w-lg text-center">
              <div className="mb-4 flex justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[12px] font-semibold text-white/85">
                  <FontAwesomeIcon
                    icon={faBolt}
                    className="h-3.5 w-3.5 text-[#F5F749]"
                  />
                  Luce
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[12px] font-semibold text-white/85">
                  <FontAwesomeIcon
                    icon={faFireFlameCurved}
                    className="h-3.5 w-3.5 text-[#FF7D00]"
                  />
                  Gas
                </span>
              </div>

              <h1 className="text-[2.05rem] font-extrabold leading-[1.08] tracking-tight sm:text-5xl">
                Assistenza luce e gas.
                <br />
                <span className="text-[#F5F749]">
                  Entra in chiamata con un tecnico
                </span>
              </h1>

              <div className="mt-6">
                <ClickArrow />
                <CallButton
                  landingName={landingName}
                  phoneDisplay={phoneDisplay}
                  phoneTel={phoneTel}
                />
              </div>

              <PartnerLogos />

              <ul className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12px] font-semibold text-white/70">
                {TRUST_CHIPS.map((chip) => (
                  <li
                    key={chip}
                    className="inline-flex items-center gap-1 whitespace-nowrap"
                  >
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="h-3.5 w-3.5 shrink-0 text-[#22C55E]"
                    />
                    {chip}
                  </li>
                ))}
              </ul>

              <p className="mt-3 text-[11px] text-white/45">{HOURS}</p>
            </div>
          </section>

          <section className="border-y border-white/10 bg-[#141416] px-4 py-8">
            <div className="mx-auto grid max-w-lg gap-3">
              {WHY_CALL.map((item) => (
                <div
                  key={item.title}
                  className="flex gap-3.5 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5"
                >
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#16A34A]/15 text-[#22C55E]">
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  </span>
                  <div className="text-left">
                    <p className="text-[15px] font-bold text-white">
                      {item.title}
                    </p>
                    <p className="mt-0.5 text-sm leading-snug text-white/60">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section
            className="mx-auto max-w-lg px-4 pb-2 pt-8 text-left"
            aria-label="Confronto offerte luce e gas"
          >
            {TOPICS.map((topic, index) => (
              <article
                key={topic.title}
                className={
                  index === 0
                    ? undefined
                    : "mt-6 border-t border-white/10 pt-6"
                }
              >
                <h2 className="text-[1.2rem] font-extrabold leading-tight tracking-tight">
                  {topic.title}
                </h2>
                {topic.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="mt-3 text-sm leading-relaxed text-white/60"
                  >
                    {paragraph}
                  </p>
                ))}
              </article>
            ))}
          </section>

          <section className="px-4 py-8" aria-label="Recensioni clienti">
            <div className="mx-auto max-w-lg">
              <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
                Chi ha già chiamato
              </p>
              <ul className="space-y-2.5">
                {REVIEWS.map((review) => (
                  <li
                    key={review.name}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[13px] font-semibold text-white/90">
                        {review.name}
                        <span className="font-medium text-white/40">
                          {" "}
                          · {review.city}
                        </span>
                      </p>
                      <span className="flex gap-0.5" aria-label="5 stelle">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <FontAwesomeIcon
                            key={index}
                            icon={faStar}
                            className="h-3 w-3 text-[#F5F749]"
                          />
                        ))}
                      </span>
                    </div>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-white/55">
                      “{review.text}”
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </main>

        <footer className="bg-[#0B0B0D] px-4 py-6 text-center text-[11px] leading-relaxed text-white/40">
          <p>GT Energie Srl · P.IVA IT02812720809</p>
          <p className="mt-1">Via Filippo Argelati 10, 20143 Milano (MI)</p>
          <p className="mt-2">
            Servizio di comparazione indipendente. Non siamo il servizio clienti
            di alcun fornitore.
          </p>
          <div className="mx-auto mt-4 max-w-lg rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-left">
            <p className="text-[10px] leading-relaxed text-white/45">
              <strong className="font-semibold text-white/65">Disclaimer:</strong>{" "}
              Servizio Bolletta è un servizio indipendente di comparazione e
              assistenza commerciale gestito da GT Energie Srl (P.IVA:
              IT02812720809, sede legale: Via Filippo Argelati 10, Milano). Non
              siamo il servizio clienti ufficiale di alcun fornitore di energia,
              gas o servizi internet. Il servizio è gratuito per l&apos;utente e
              non implica alcun obbligo di sottoscrizione. L&apos;eventuale
              stipula di un contratto con un fornitore è una decisione libera e
              autonoma del cliente. In alcuni casi Servizio Bolletta potrebbe
              percepire una commissione commerciale dai fornitori partner in
              caso di attivazione. Le informazioni e le offerte presenti su
              questo sito hanno carattere puramente informativo e possono
              variare.
            </p>
          </div>
          <p className="mt-3">
            <Link to="/privacy-policy" className="underline hover:text-white">
              Privacy
            </Link>
            {" · "}
            <Link to="/cookie-policy" className="underline hover:text-white">
              Cookie
            </Link>
            {" · "}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("sb-open-consent"))}
              className="underline hover:text-white"
            >
              Gestisci cookie
            </button>
          </p>
        </footer>
      </div>

      {cookieResolved && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-black/20 bg-[#0B0B0D]/96 px-3 py-2.5 pb-[max(0.65rem,env(safe-area-inset-bottom))] backdrop-blur-md">
          <div className="mx-auto flex max-w-lg items-center gap-2.5">
            <span
              className="ldc-bar-arrow pointer-events-none shrink-0"
              aria-hidden
            >
              <svg viewBox="0 0 36 28" className="h-6 w-8" fill="none">
                <path
                  d="M4 14h24M20 5l12 9-12 9"
                  stroke="#F5F749"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <CallButton
              landingName={landingName}
              phoneDisplay={phoneDisplay}
              phoneTel={phoneTel}
              size="bar"
            />
          </div>
        </div>
      )}

      <style>{`
        .ldc-arrow {
          animation: ldc-arrow-bounce 1.35s ease-in-out infinite;
        }
        .ldc-call-btn {
          animation: ldc-call-glow 2.2s ease-in-out infinite;
        }
        .ldc-call-shine {
          background: linear-gradient(
            110deg,
            transparent 35%,
            rgba(255, 255, 255, 0.22) 50%,
            transparent 65%
          );
          transform: translateX(-100%);
          animation: ldc-shine 3.4s ease-in-out infinite;
        }
        .ldc-bar-arrow {
          animation: ldc-bar-nudge 1.2s ease-in-out infinite;
        }
        @keyframes ldc-arrow-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(7px); }
        }
        @keyframes ldc-call-glow {
          0%, 100% { box-shadow: 0 16px 40px -8px rgba(22, 163, 74, 0.55); }
          50% { box-shadow: 0 18px 48px -4px rgba(22, 163, 74, 0.85); }
        }
        @keyframes ldc-shine {
          0%, 55% { transform: translateX(-100%); }
          80%, 100% { transform: translateX(100%); }
        }
        @keyframes ldc-bar-nudge {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(6px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ldc-arrow,
          .ldc-call-btn,
          .ldc-call-shine,
          .ldc-bar-arrow {
            animation: none;
          }
        }
      `}</style>
    </>
  );
};

export default DirectCallLandingView;
